/**
 * Realtime transport. SPEC.md §15, §16.
 *
 * One socket per browser tab, fully typed by the shared event maps. Everything
 * subtle about this file comes from four spec rules:
 *
 *  - Sequenced mutations are strictly contiguous. A gap means state was missed,
 *    so the client resyncs rather than guessing what it lost. §15.2
 *  - player:connection-changed carries no sequence at all, because a flapping
 *    mobile radio must not look like data loss. It is applied unconditionally
 *    and never advances the sequence. §8.5
 *  - Reconnecting re-authenticates with the reconnect token and compares
 *    sequences; controls come back only once authoritative state is known. §16.3
 *  - The page can be suspended, frozen, or resumed on a different network. On
 *    return the transport reattaches instead of trusting what it has. §16.1
 *
 * There is no offline write queue. §16.4
 */

import { io, type Socket } from 'socket.io-client';
import {
  CLIENT_EVENTS,
  ERROR_MESSAGES,
  SERVER_EVENTS,
  sequenceSpanStart,
  type Ack,
  type AckFn,
  type AttachSessionInput,
  type ClientToServerEvents,
  type ConnectionState,
  type ErrorCode,
  type ServerToClientEvents,
  type SessionSnapshot,
  type SyncRequiredReason,
  type WireError,
  winnerCelebration,
} from '@card-counter/shared';
import { useConnectionStore } from '@/stores/connection-store';
import { departedPlayerIds, hasSnapshot, useSessionStore } from '@/stores/session-store';
import {
  HAND_ENTRY_TOAST,
  dismissToast,
  endCelebrationFor,
  startDrinkCelebration,
  startRoundExplosion,
  startWinnerCelebration,
} from '@/stores/ui-store';

export type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export type ClientEventName = keyof ClientToServerEvents;
export type ClientEventPayload<E extends ClientEventName> = Parameters<
  ClientToServerEvents[E]
>[0];
export type ClientEventAckData<E extends ClientEventName> =
  Parameters<ClientToServerEvents[E]>[1] extends AckFn<infer T> ? T : never;

/** Long enough for a slow cellular round trip, short enough to retry by hand. */
const ACK_TIMEOUT_MS = 8_000;
/** Failures before we stop calling it "reconnecting" and admit we are offline. */
const ATTEMPTS_BEFORE_OFFLINE = 3;

/** Attach failures that no amount of retrying will fix. §35 */
const FATAL_ATTACH_CODES: readonly ErrorCode[] = [
  'INVALID_TOKEN',
  'PLAYER_REMOVED',
  'SESSION_NOT_FOUND',
  'SESSION_ENDED',
];

interface Attachment {
  sessionId: string;
  playerToken: string;
}

let socket: AppSocket | null = null;
let attachment: Attachment | null = null;
let joined = false;
let resyncInFlight = false;
let failedAttempts = 0;
let environmentListenersInstalled = false;
let attachRetryTimer: ReturnType<typeof setTimeout> | null = null;

const errorListeners = new Set<(error: WireError) => void>();

// --- Error fan-out -----------------------------------------------------------

/**
 * Server-pushed errors and fatal attach failures. Kept as a subscription so this
 * module stays free of UI concerns; useSession turns them into toasts. §35
 */
export function onWireError(listener: (error: WireError) => void): () => void {
  errorListeners.add(listener);
  return () => {
    errorListeners.delete(listener);
  };
}

function emitWireError(error: WireError): void {
  for (const listener of [...errorListeners]) listener(error);
}

function internalError(): WireError {
  return { code: 'INTERNAL', message: ERROR_MESSAGES.INTERNAL };
}

// --- Connection state --------------------------------------------------------

function setConnectionState(next: ConnectionState): void {
  useConnectionStore.getState().setConnectionState(next);
}

function browserOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

// --- Emitting ----------------------------------------------------------------

/**
 * Emits and resolves with the shared Ack contract. Failures are values, not
 * exceptions, so a caller can show copy next to the control that was touched.
 *
 * A timed-out ack resolves as INTERNAL, whose copy is already "Couldn't record
 * that point change. Reconnecting…". §35
 */
export function emitWithAck<E extends ClientEventName>(
  event: E,
  payload: ClientEventPayload<E>,
): Promise<Ack<ClientEventAckData<E>>> {
  const active = socket;
  if (!active || active.disconnected) {
    return Promise.resolve({ ok: false, error: internalError() });
  }

  return new Promise((resolve) => {
    let settled = false;

    const finish = (response: Ack<ClientEventAckData<E>>): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(response);
    };

    const timer = setTimeout(() => {
      finish({ ok: false, error: internalError() });
    }, ACK_TIMEOUT_MS);

    // The typed emit signature can't be expressed generically over the event
    // map, so the cast is confined to this one call. It must stay bound to the
    // socket: socket.io reads `this._opts` to resolve the ack timeout, so a
    // detached `emit` throws before anything reaches the wire.
    const emit = active.emit.bind(active) as unknown as (
      name: string,
      data: unknown,
      ack: (response: Ack<ClientEventAckData<E>>) => void,
    ) => void;
    emit(event, payload, finish);
  });
}

// --- Snapshot / sequence handling --------------------------------------------

function applySnapshot(snapshot: SessionSnapshot): void {
  useSessionStore.getState().applySnapshot(snapshot);
  const connection = useConnectionStore.getState();
  connection.setLastSequence(snapshot.sequence);
  connection.setResyncing(false);
  connection.setConnectionState('connected');
}

/**
 * Replaces local canonical state from the server. §15.2 step 2–3
 * Concurrent callers share one request; a gap and a sync:required arriving
 * together must not fetch twice.
 */
export async function requestSnapshot(): Promise<void> {
  if (!socket || socket.disconnected || resyncInFlight) return;
  resyncInFlight = true;
  try {
    const ack = await emitWithAck(CLIENT_EVENTS.SNAPSHOT_REQUEST, null);
    if (ack.ok) {
      applySnapshot(ack.data.snapshot);
    } else {
      emitWireError(ack.error);
      setConnectionState('reconnecting');
    }
  } finally {
    resyncInFlight = false;
  }
}

/** Marks local state stale and fetches authoritative state. §15.2, §14.3 */
function markStale(_reason: SyncRequiredReason): void {
  const connection = useConnectionStore.getState();
  connection.setResyncing(true);
  connection.setConnectionState('resyncing');
  void requestSnapshot();
}

/**
 * Applies a mutation covering a span of sequences, or resyncs instead.
 * Contiguity is checked here and nowhere else. §15.2
 *
 * Most mutations produce exactly one sequence, so `from` and `to` are the same
 * number. Two do not, and both are corrections: a report takes one sequence for
 * itself and then one per row it caused, and deleting a hand does the same for
 * the rows it cancels. Checking only the last of them against "the next one I
 * expect" reads every settled hand as a gap and resyncs the whole table for
 * nothing — which is precisely the arithmetic the broadcaster does on the way
 * out, mirrored here on the way in. §15.2, §3.1, §12.5
 *
 * "Do I have state to apply this onto?" is hasSnapshot, not `sequence > 0`: a
 * table sits at sequence 0 until its first mutation, so reading 0 as "no state"
 * would resync on the very first event of every new game.
 */
function sequencedSpan(from: number, to: number, apply: () => void): void {
  const state = useSessionStore.getState();
  useConnectionStore.getState().setLastSequence(to);

  if (!hasSnapshot(state)) {
    markStale('sequence-gap');
    return;
  }
  if (to <= state.sequence) return; // already applied — a duplicate delivery
  if (from !== state.sequence + 1) {
    markStale('sequence-gap');
    return;
  }
  apply();
}

function sequenced(sequence: number, apply: () => void): void {
  sequencedSpan(sequence, sequence, apply);
}

// --- Attach ------------------------------------------------------------------

/**
 * Authenticates the socket against the session. The first attach joins and takes
 * a full snapshot; later ones reconnect with the applied sequence so the server
 * can skip the snapshot when nothing was missed. §16.3
 */
async function attach(): Promise<void> {
  const active = socket;
  const current = attachment;
  if (!active || !current) return;

  if (attachRetryTimer) {
    clearTimeout(attachRetryTimer);
    attachRetryTimer = null;
  }

  const applied = useSessionStore.getState().sequence;
  const payload: AttachSessionInput = {
    sessionId: current.sessionId,
    playerToken: current.playerToken,
    lastSequence: applied > 0 ? applied : undefined,
  };

  setConnectionState('resyncing');
  useConnectionStore.getState().setResyncing(true);

  if (!joined) {
    const ack = await emitWithAck(CLIENT_EVENTS.SESSION_JOIN, payload);
    if (!ack.ok) {
      handleAttachFailure(ack.error);
      return;
    }
    joined = true;
    applySnapshot(ack.data.snapshot);
    return;
  }

  const ack = await emitWithAck(CLIENT_EVENTS.SESSION_RECONNECT, payload);
  if (!ack.ok) {
    handleAttachFailure(ack.error);
    return;
  }

  if (ack.data.snapshot) {
    applySnapshot(ack.data.snapshot);
    return;
  }

  if (ack.data.sequence !== applied) {
    // The server moved on but sent no snapshot: never guess the difference. §15.2
    markStale('reattached');
    return;
  }

  const connection = useConnectionStore.getState();
  connection.setLastSequence(ack.data.sequence);
  connection.setResyncing(false);
  connection.setConnectionState('connected');
}

function handleAttachFailure(error: WireError): void {
  emitWireError(error);
  if (FATAL_ATTACH_CODES.includes(error.code)) {
    // Retrying a rejected identity just burns battery; the UI sends the player
    // back to the join flow. §35
    joined = false;
    socket?.disconnect();
    setConnectionState('offline');
    return;
  }
  setConnectionState('reconnecting');
  // A timed-out or transiently failed attach leaves a live socket that has never
  // joined a room, so nothing else would ever wake it. §16.3
  scheduleAttachRetry();
}

const ATTACH_RETRY_DELAY_MS = 1_500;

function scheduleAttachRetry(): void {
  if (attachRetryTimer || !attachment) return;
  attachRetryTimer = setTimeout(() => {
    attachRetryTimer = null;
    if (!socket || !attachment) return;
    if (socket.connected) void attach();
    else socket.connect();
  }, ATTACH_RETRY_DELAY_MS);
}

// --- Handlers ----------------------------------------------------------------

function registerHandlers(active: AppSocket): void {
  const store = () => useSessionStore.getState();

  active.on('connect', () => {
    failedAttempts = 0;
    void attach();
  });

  active.on('connect_error', () => {
    failedAttempts += 1;
    setConnectionState(
      browserOffline() || failedAttempts >= ATTEMPTS_BEFORE_OFFLINE ? 'offline' : 'reconnecting',
    );
  });

  active.on('disconnect', (reason) => {
    // Our own teardown; nothing to report.
    if (reason === 'io client disconnect') return;
    setConnectionState(browserOffline() ? 'offline' : 'reconnecting');
  });

  active.io.on('reconnect_attempt', () => {
    setConnectionState(browserOffline() ? 'offline' : 'reconnecting');
  });

  active.on(SERVER_EVENTS.SESSION_SNAPSHOT, (payload) => {
    applySnapshot(payload.snapshot);
  });

  active.on(SERVER_EVENTS.SESSION_UPDATED, (payload) => {
    sequenced(payload.sequence, () => store().applySessionUpdated(payload));
  });

  active.on(SERVER_EVENTS.PLAYER_JOINED, (payload) => {
    sequenced(payload.sequence, () => store().applyPlayerJoined(payload));
  });

  active.on(SERVER_EVENTS.PLAYER_UPDATED, (payload) => {
    sequenced(payload.sequence, () => store().applyPlayerUpdated(payload));
  });

  active.on(SERVER_EVENTS.PLAYER_SEAT_CHANGED, (payload) => {
    sequenced(payload.sequence, () => store().applyPlayerSeatChanged(payload));
  });

  /**
   * A player left the table. §31
   *
   * Whatever they were being cheered for goes with them: a removed player has no
   * podium for their avatar to dance on, so the effect is already gone from the
   * screen — but its thirty-second timer is not, and the next thing the table
   * cheers must not inherit what is left of a clock belonging to somebody who is
   * no longer here. §4.5, §12.1
   */
  active.on(SERVER_EVENTS.PLAYER_REMOVED, (payload) => {
    sequenced(payload.sequence, () => {
      store().applyPlayerRemoved(payload);
      endCelebrationFor(payload.playerId);
    });
  });

  // No sequence, no gap check, no effect on the applied sequence. §8.5
  active.on(SERVER_EVENTS.PLAYER_CONNECTION_CHANGED, (payload) => {
    store().applyPlayerConnectionChanged(payload);
  });

  active.on(SERVER_EVENTS.ROUND_STARTED, (payload) => {
    sequenced(payload.sequence, () => store().applyRoundStarted(payload));
  });

  /**
   * A completed hand: the one moment the table turns over, and — when somebody
   * was silent for it — the one moment it celebrates. §4.4, §4.5
   *
   * Started here rather than from a `lastRoundSummary` effect because this is
   * the only place that means "the server has just confirmed a completion".
   * State can arrive at the same summary again — a remount reads it, a resync
   * lands on top of it — and none of those are the hand ending. Inside
   * `sequenced`, so a delivery that arrives out of order or twice is dropped
   * before it can fire for a hand this device never applied. §15.2
   *
   * The two effects are deliberately not one. Every completed hand turns over;
   * only a hand exactly one player was silent for has a winner, and a hand where
   * somebody paid everybody was won by nobody (§3.2). Reading either from the
   * other would put a face on a hand that had no result, or leave a hand nobody
   * won ending with no sign that it had. §4.4, §4.5
   */
  active.on(SERVER_EVENTS.ROUND_COMPLETED, (payload) => {
    sequenced(payload.sequence, () => {
      store().applyRoundCompleted(payload);

      /*
        Whatever this device's own report was offering to withdraw, it cannot any
        more: the hand it belonged to has ended, and the server would refuse the
        UNDO. The way back from here is the host deleting the hand. §12.5, §12.1

        Taken down here, on the frame, for the same reason the effects are — a
        notice that outlived its hand used to be dismissed by the "HAND X closed"
        line arriving on top of it, and that line has gone.
      */
      dismissToast(HAND_ENTRY_TOAST);

      startRoundExplosion({
        roundId: payload.round.id,
        handNumber: payload.round.number,
      });

      const celebration = winnerCelebration({
        round: payload.round,
        entries: payload.summary.entries,
        // A hand settled by somebody leaving still has a winner to cheer, and
        // the face on screen must be the one the ledger paid. §31, §60
        departedPlayerIds: departedPlayerIds(useSessionStore.getState()),
        drinkingMode: useSessionStore.getState().session?.drinkingMode ?? false,
      });
      if (celebration) startWinnerCelebration(celebration);
    });
  });

  /**
   * The host named who deals. §9.6
   *
   * One sequence, because it moves no points and writes no ledger row — the
   * round's offset changed and nothing else did. Every device re-derives the
   * marker from it with the same `dealerPlayerId` it always used, so no dealer
   * id crosses the wire and there is nothing here to disagree with the ring. §60
   */
  active.on(SERVER_EVENTS.ROUND_DEALER_CHANGED, (payload) => {
    sequenced(payload.sequence, () => store().applyRoundDealerChanged(payload));
  });

  /**
   * The host deleted a hand. Same span shape as a report — one sequence for the
   * deletion, one per row it cancelled — because it is the same kind of
   * correction. §12.5, §15.2
   */
  active.on(SERVER_EVENTS.ROUND_VOIDED, (payload) => {
    sequencedSpan(sequenceSpanStart(payload), payload.sequence, () =>
      store().applyRoundVoided(payload),
    );
  });

  /**
   * One reported loss is one sequenced mutation, however many rows it moved.
   * That span is one sequence for the report plus one per row — and a report
   * that moved nothing at all still arrives, because "somebody reported" is
   * what the rest of the table is waiting to hear. §15.2, §3.1
   *
   * The report that ends the hand is followed by `round:completed` and
   * `round:started`, each one sequence further on, so the run stays
   * contiguous. §4.3
   */
  active.on(SERVER_EVENTS.HAND_ENTRY_CHANGED, (payload) => {
    sequencedSpan(sequenceSpanStart(payload), payload.sequence, () =>
      store().applyHandEntryChanged(payload),
    );
  });

  /**
   * A finished drink, in drinking mode. One row, so one sequence. §4.6, §15.2
   *
   * The cheer is started from here for exactly the reason the winner's is: this
   * frame is the only thing that means "somebody has just finished a drink".
   * `handAdjustments` is a *state*, and state is re-observed constantly — a
   * remount reads it, a resync lands on top of it — so driving the effect from
   * it would cheer again for a drink finished ten minutes ago. Inside
   * `sequenced`, so a delivery that arrives twice or out of order is dropped
   * before it can cheer at all. §4.5, §12.1
   */
  active.on(SERVER_EVENTS.DRINK_RECORDED, (payload) => {
    sequenced(payload.sequence, () => {
      store().applyDrinkRecorded(payload);
      startDrinkCelebration({
        adjustmentId: payload.adjustment.id,
        playerId: payload.playerId,
      });
    });
  });

  active.on(SERVER_EVENTS.SYNC_REQUIRED, (payload) => {
    useConnectionStore.getState().setLastSequence(payload.sequence);
    markStale(payload.reason);
  });

  active.on(SERVER_EVENTS.ERROR, (payload) => {
    emitWireError(payload);
  });
}

// --- Environment (§16.1) -----------------------------------------------------

function onVisibilityChange(): void {
  if (document.visibilityState !== 'visible' || !attachment) return;
  // The page may have been frozen for minutes. Either the socket needs waking or
  // the state needs proving, and reattaching does both cheaply. §16.1, §16.3
  if (!socket || socket.disconnected) {
    socket?.connect();
    return;
  }
  void attach();
}

function onOnline(): void {
  if (!attachment) return;
  setConnectionState('reconnecting');
  if (socket && socket.disconnected) socket.connect();
}

function onOffline(): void {
  if (!attachment) return;
  setConnectionState('offline');
}

function installEnvironmentListeners(): void {
  if (environmentListenersInstalled || typeof window === 'undefined') return;
  environmentListenersInstalled = true;
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
}

function removeEnvironmentListeners(): void {
  if (!environmentListenersInstalled || typeof window === 'undefined') return;
  environmentListenersInstalled = false;
  document.removeEventListener('visibilitychange', onVisibilityChange);
  window.removeEventListener('online', onOnline);
  window.removeEventListener('offline', onOffline);
}

// --- Lifecycle ---------------------------------------------------------------

/**
 * Opens (or reuses) the socket for a session and authenticates it with the
 * stored reconnect token. Idempotent, so several mounted components can call it.
 */
export function connectSession(params: Attachment): AppSocket {
  if (
    socket &&
    attachment &&
    attachment.sessionId === params.sessionId &&
    attachment.playerToken === params.playerToken
  ) {
    if (socket.disconnected) socket.connect();
    return socket;
  }

  disconnectSession();

  attachment = { ...params };
  joined = false;
  failedAttempts = 0;
  setConnectionState('reconnecting');
  installEnvironmentListeners();

  const active: AppSocket = io({
    path: '/socket.io',
    // Authenticated at the handshake as well as in session:join, so the server
    // can reject an unknown token before it joins any room. §27
    auth: { sessionId: params.sessionId, playerToken: params.playerToken },
    reconnection: true,
    reconnectionDelay: 400,
    reconnectionDelayMax: 4_000,
    timeout: ACK_TIMEOUT_MS,
    withCredentials: false,
  });

  socket = active;
  registerHandlers(active);
  return active;
}

/** Tears the socket down and clears everything derived from it. */
export function disconnectSession(): void {
  const active = socket;
  socket = null;
  attachment = null;
  joined = false;
  resyncInFlight = false;
  failedAttempts = 0;
  if (attachRetryTimer) {
    clearTimeout(attachRetryTimer);
    attachRetryTimer = null;
  }
  removeEnvironmentListeners();

  if (active) {
    active.removeAllListeners();
    active.io.off('reconnect_attempt');
    active.disconnect();
  }

  useSessionStore.getState().reset();
  useConnectionStore.getState().reset();
}
